import React, { useEffect, useState } from "react"
import { Button, Col, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import Switch from "components/Shared/Switch"
import { DatePicker } from "rsuite"
import Select from "react-select"
import { getAllCustomersList } from "store/actions/customersAction"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { sendNotificationSchema } from "components/Common/validation"
import { sendNotification, searchCustomersByPhone } from "store/actions/pushNotificationAction"
import { showAlert } from "store/reducers/alertReducer"
import { disablePastDates } from "components/Common/constants"
import TextError from "components/Shared/TextError"
import customerNotificationTemplate from "assets/file/customerNotificationTemplate.csv"
import * as XLSX from "xlsx"

const initialValues = {
  type: "all",
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  isScheduled: false,
  scheduledTime: "",
  customers: [],
  csvFile: null,
}

// Helper function to convert scientific notation to regular phone number
const convertScientificToNumber = (phoneNumber) => {
  const originalPhone = String(phoneNumber).trim();

  // Check if it's in scientific notation (e.g., 9.66593E+11)
  if (/\d+\.?\d*e[+-]\d+/i.test(originalPhone)) {
    try {
      // Use string manipulation to avoid precision loss
      const [base, exp] = originalPhone.toLowerCase().split('e');
      const exponent = parseInt(exp);

      if (exponent >= 0) {
        // Remove decimal point and pad with zeros
        let result = base.replace('.', '');
        const decimalPlaces = base.includes('.') ? base.split('.')[1].length : 0;
        const zerosToAdd = exponent - decimalPlaces;

        if (zerosToAdd > 0) {
          result += '0'.repeat(zerosToAdd);
        }

        return result;
      }
    } catch (error) {
      return originalPhone;
    }
  }

  return originalPhone;
};

// Function to generate Excel template with proper text formatting
const downloadExcelTemplate = () => {
  // Create sample data
  const data = [
    { "s.no": 1, "phone": 966592535420 },
    { "s.no": 2, "phone": 966593123456 }
  ];

  // Create worksheet
  const ws = XLSX.utils.json_to_sheet(data);

  // Set column widths
  ws['!cols'] = [
    { width: 10 }, // s.no column
    { width: 15 }  // phone column
  ];

  // Format phone column as text to prevent scientific notation
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: 1 }); // Column B (phone)
    if (ws[cellAddress]) {
      ws[cellAddress].z = '@'; // Text format
      ws[cellAddress].t = 's'; // String type
      ws[cellAddress].v = String(ws[cellAddress].v); // Convert value to string
    }
  }

  // Create workbook and add worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Customer Notification Template");

  // Download the file
  XLSX.writeFile(wb, "customer_notification_template.xlsx");
};

const SendNotification = ({ t }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { customersDropdownList } = useSelector(state => state.Customers)
  const { isLoading } = useSelector(state => state.PushNotifications)

  const [customerOptions, setCustomerOptions] = useState([])

  useEffect(() => {
    if (customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}, ${
          item?.phoneNumber ? item?.phoneNumber : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
      //   console.log(customersDropdownList)
    }
  }, [customersDropdownList])

  const fetchCustomersList = keyword => {
    const payload = {
      keyword,
    }
    dispatch(getAllCustomersList(payload))
  }

  const handleFileUpload = (file, setFieldValue, currentCustomers = []) => {
    if (!file) return

    const fileExtension = file.name.toLowerCase().split('.').pop()

    if (fileExtension === 'csv') {
      handleCSVUpload(file, setFieldValue, currentCustomers)
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      handleExcelUpload(file, setFieldValue, currentCustomers)
    } else {
      dispatch(showAlert({
        text: "Please upload a CSV or Excel file",
        type: "error"
      }))
    }
  }

  const handleCSVUpload = (file, setFieldValue, currentCustomers = []) => {
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const csv = e.target.result
        const lines = csv.split('\n')
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

        // Find phone column index
        const phoneIndex = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('number'))

        if (phoneIndex === -1) {
          dispatch(showAlert({
            text: "CSV must contain a phone number column",
            type: "error"
          }))
          return
        }

        const phoneNumbers = []

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim()
          if (!line) continue

          const values = line.split(',').map(v => v.trim())
          let phone = values[phoneIndex]

          if (phone) {
            // Remove quotes if present and keep as string to prevent number conversion
            phone = phone.replace(/['"]/g, '').trim()

            // Remove + if present
            if (phone.startsWith('+')) {
              phone = phone.substring(1)
            }

            // Convert scientific notation if present
            if (/e[+-]/i.test(phone)) {
              phone = convertScientificToNumber(phone)
            }

            // Ensure it's a valid phone number (only digits)
            if (/^\d+$/.test(phone)) {
              phoneNumbers.push(phone)
            }
          }
        }

        // Search customers by phone numbers using backend API
        if (phoneNumbers.length > 0) {
          fetchCustomersForMatching(phoneNumbers, setFieldValue, currentCustomers)
        } else {
          dispatch(showAlert({
            text: "No phone numbers found in CSV",
            type: "warning"
          }))
        }
      } catch (error) {
        dispatch(showAlert({
          text: "Error parsing CSV file",
          type: "error"
        }))
      }
    }

    reader.readAsText(file)
  }

  const handleExcelUpload = (file, setFieldValue, currentCustomers = []) => {
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[worksheetName]

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

        if (jsonData.length < 2) {
          dispatch(showAlert({
            text: "Excel file must contain headers and data",
            type: "error"
          }))
          return
        }

        // Find phone column index
        const headers = jsonData[0].map(h => String(h).trim().toLowerCase())
        const phoneIndex = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('number'))

        if (phoneIndex === -1) {
          dispatch(showAlert({
            text: "Excel must contain a phone number column",
            type: "error"
          }))
          return
        }

        const phoneNumbers = []

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i]
          let phone = row[phoneIndex]

          if (phone) {
            // Convert to string and clean
            phone = String(phone).trim()

            // Remove + if present
            if (phone.startsWith('+')) {
              phone = phone.substring(1)
            }

            // Convert scientific notation if present
            if (/e[+-]/i.test(phone)) {
              phone = convertScientificToNumber(phone)
            }

            // Ensure it's a valid phone number (only digits)
            if (/^\d+$/.test(phone)) {
              phoneNumbers.push(phone)
            }
          }
        }

        // Search customers by phone numbers using backend API
        if (phoneNumbers.length > 0) {
          fetchCustomersForMatching(phoneNumbers, setFieldValue, currentCustomers)
        } else {
          dispatch(showAlert({
            text: "No phone numbers found in Excel file",
            type: "warning"
          }))
        }
      } catch (error) {
        dispatch(showAlert({
          text: "Error parsing Excel file",
          type: "error"
        }))
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const fetchCustomersForMatching = (phoneNumbers, setFieldValue, currentCustomers = []) => {
    // Ensure all phone numbers are properly converted from scientific notation
    const processedPhoneNumbers = phoneNumbers.map(phone => convertScientificToNumber(phone))

    dispatch(searchCustomersByPhone({ phoneNumbers: processedPhoneNumbers }))
      .then(action => {
        const { statusCode, status, data, matchedCount } = action?.payload

        if (statusCode === 200 && status && data) {
          if (matchedCount > 0) {
            // Merge with existing selections
            const existingCustomers = currentCustomers || []
            const newCustomers = data.filter(newCustomer =>
              !existingCustomers.some(existing => existing.value === newCustomer.value)
            )

            setFieldValue("customers", [...existingCustomers, ...newCustomers])
            dispatch(showAlert({
              text: `${matchedCount} ${t("customers auto-selected from CSV")}`,
              type: "success"
            }))
          } else {
            dispatch(showAlert({
              text: t("No matching customers found for the phone numbers in CSV"),
              type: "warning"
            }))
          }
        } else {
          dispatch(showAlert({
            text: t("Failed to search customers by phone numbers"),
            type: "error"
          }))
        }
      })
      .catch(error => {
        dispatch(showAlert({
          text: "Error searching customers by phone numbers",
          type: "error"
        }))
      })
  }

  const handleOnSubmit = (values, { resetForm }) => {
    const {
      type,
      titleEn,
      titleAr,
      bodyEn,
      bodyAr,
      isScheduled,
      scheduledTime,
      customers,
    } = values

    const updatedCustomers = customers ? customers?.map(item => item.value) : []

    const payload = {
      type: type, //all or specific-customers
      title: {
        en: titleEn,
        ar: titleAr,
      },
      body: {
        en: bodyEn,
        ar: bodyAr,
      },
      sound: "default",
      isScheduled: isScheduled,
      scheduledTime: scheduledTime,
      customers: updatedCustomers,
      data: {},
    }

    dispatch(sendNotification(payload))
      .then(action => {
        const { statusCode, status, } = action?.payload
        const msg = action?.payload?.msg || action?.payload?.message || "Unknown error";
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          resetForm()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <Row>
      <Col sm="12">
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={sendNotificationSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="titleEn" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Title(English)")}
                    </label>
                    <Field
                      name="titleEn"
                      type="text"
                      id="titleEn"
                      placeholder={t("Title(English)")}
                      className={`form-control ${
                        touched.titleEn && errors.titleEn ? "is-invalid" : ""
                      }`}
                    />
                    <TextError
                      name="titleEn"
                      
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="titleAr" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Title(عربي)")}
                    </label>
                    <Field
                      name="titleAr"
                      type="text"
                      id="titleAr"
                      style={{ direction: "rtl" }}
                      placeholder={t("Title(عربي)")}
                      className={`form-control ${
                        touched.titleAr && errors.titleAr ? "is-invalid" : ""
                      }`}
                    />
                    <TextError
                      name="titleAr"
                      
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="bodyEn" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Message(English)")}
                    </label>
                    <Field
                      as="textarea"
                      id="bodyEn"
                      name="bodyEn"
                      rows={3}
                      placeholder={t("Message(English)")}
                      className={`form-control ${
                        touched.bodyEn && errors.bodyEn ? "is-invalid" : ""
                      }`}
                    />
                    <TextError
                      name="bodyEn"
                      
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="bodyAr" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Message(عربي)")}
                    </label>
                    <Field
                      as="textarea"
                      id="bodyAr"
                      name="bodyAr"
                      rows={3}
                      placeholder={t("Message(عربي)")}
                      style={{ direction: "rtl" }}
                      className={`form-control ${
                        touched.bodyAr && errors.bodyAr ? "is-invalid" : ""
                      }`}
                    />
                    <TextError
                      name="bodyAr"
                      
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mt-3">
                    <label htmlFor="name">{t("Select Type")}</label>
                    <div className="d-flex ">
                      <div className="form-check mx-2">
                        <Field
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="all"
                          value="all"
                          onClick={() => {
                            setFieldValue("type", "all")
                          }}
                        />
                        <label className="form-check-label" htmlFor="all">
                          {t("All")}
                        </label>
                      </div>
                      <div className="form-check mx-2">
                        <Field
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="specific-customers"
                          value="specific-customers"
                          onClick={() => {
                            setFieldValue("type", "specific-customers")
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="specific-customers"
                        >
                          {t("Specific Customers")}
                        </label>
                      </div>
                    </div>
                  </div>
                </Col>
                {values.type === "specific-customers" && (
                  <>
                    <Col md={6}>
                      <label htmlFor="">
                        <span className="text-danger">*</span>{" "}
                        {t("Please Type To Search Customer")}
                      </label>
                      <Select
                        value={values.customers}
                        isMulti={true}
                        onChange={selectedValue => {
                          setFieldValue("customers", selectedValue)
                        }}
                        options={customerOptions?.map(item => ({
                          label: item.label,
                          value: item.value,
                        }))}
                        onInputChange={value => fetchCustomersList(value)}
                        placeholder={t("Please Type To Search Customer")}
                        className={`select2-selection ${
                          touched.customers && errors.customers
                            ? "danger_color_border"
                            : ""
                        } `}
                      />
                      <TextError
                        name="customers"

                      />
                    </Col>
                    <Col md={6}>
                      <div className="form_field">
                        <label htmlFor="csvFile" className="col col-form-label">
                          {t("Or Upload File")}
                        </label>
                        <input
                          name="csvFile"
                          type="file"
                          id="csvFile"
                          accept=".csv,.xlsx,.xls"
                          className="form-control"
                          onChange={(event) => {
                            setFieldValue("csvFile", event.currentTarget.files[0])
                            handleFileUpload(event.currentTarget.files[0], setFieldValue, values.customers)
                          }}
                        />
                        <small className="text-muted">
                          {t("Upload Excel or CSV file with phone numbers to auto-select customers")}
                        </small>
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={downloadExcelTemplate}
                            className="btn btn-outline-primary btn-sm"
                          >
                            {t("Download Template")}
                          </button>
                        </div>
                      </div>
                    </Col>
                  </>
                )}
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="">{t("Scheduled Notification")}</label>
                    <Switch
                      isChecked={values.isScheduled}
                      onClickHandler={() => {
                        setFieldValue("isScheduled", !values.isScheduled)
                      }}
                    />
                  </div>
                </Col>
                {values.isScheduled && (
                  <Col md={6}>
                    <div className="form_field full_rs_picker">
                      <label htmlFor="endTime">
                        <span className="text-danger">*</span>{" "}
                        {t("Schedule Date & Time")}
                      </label>
                      <DatePicker
                        showTime={{ format: "HH:mm" }} // Show time picker
                        format="yyyy-MM-dd HH:mm" // Custom format for displaying date and time
                        onChange={value => {
                          setFieldValue("scheduledTime", value)
                        }}
                        value={values.scheduledTime}
                        placeholder=" 2024-07-16 14:30"
                        disabledDate={disablePastDates}
                      />

                      <TextError
                        name="scheduledTime"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </Col>
                )}
                <Col md={12}>
                  <div className="d-flex align-items-center justify-content-end mt-5">
                    {/* <Button
                      type="button"
                      color="secondary"
                      className=" mx-2"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button> */}
                    <Button
                      color="success"
                      type="submit"
                      style={{ width: "fit-content" }}
                      disabled={isLoading}
                    >
                      {values.isScheduled ? t("Save") : t("Send")}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  )
}

export default SendNotification
